import React, { useEffect, useState } from 'react';
import { Dialog, DialogActions, DialogBody, DialogTitle } from '../../components/Dialog';
import { Button } from '../../components/Button';
import { Select } from '../../components/Select';
import { Input } from '../../components/Input';
import { Checkbox } from '../../components/Checkbox';
import { Text } from '../../components/Text';
import {
    ArrowLeftIcon,
    CheckCircleIcon,
    CircleStackIcon,
    ListBulletIcon,
    SparklesIcon,
    TableCellsIcon,
    VariableIcon,
} from '@heroicons/react/20/solid';
import showErrorToast from '../../components/Toasts/ShowErrorToast';
import { describeFill } from './fillUtils';
import { AiReportColumnRule, AiReportCustomFill, AiReportTemplateColumn } from '../../models/AiReportHelper';

type Mode = 'source' | 'constant' | 'ai' | 'dataset' | 'dropdown' | 'checkbox';

const METHODS: { mode: Mode; icon: React.ReactNode; title: string; description: string }[] = [
    {
        mode: 'source', icon: <TableCellsIcon className="size-5" />,
        title: 'Map from a column in my source file',
        description: 'Copy the value straight from one of your uploaded report\'s columns.',
    },
    {
        mode: 'constant', icon: <VariableIcon className="size-5" />,
        title: 'Always use the same value',
        description: 'Every row in this column gets the exact same text.',
    },
    {
        mode: 'ai', icon: <SparklesIcon className="size-5" />,
        title: 'Ask AI to figure it out',
        description: 'Describe what should go here in plain English, e.g. "always Sarawak".',
    },
    {
        mode: 'dataset', icon: <CircleStackIcon className="size-5" />,
        title: 'Fill from a Data Set',
        description: 'Look up a value from one of your data sets by matching a column.',
    },
    {
        mode: 'dropdown', icon: <ListBulletIcon className="size-5" />,
        title: 'Fill from a dropdown of options',
        description: 'Pick one fixed value from a short list of choices.',
    },
    {
        mode: 'checkbox', icon: <CheckCircleIcon className="size-5" />,
        title: 'Fill with a Yes/No checkbox',
        description: 'Every row gets either "Yes" or "No".',
    },
];

interface ColumnFillWizardProps {
    open: boolean;
    onClose: () => void;
    column: AiReportTemplateColumn | null;
    rule: AiReportColumnRule | undefined;
    sourceHeaders: string[];
    conversionId: number | null;
    resolveFillInstruction: (conversionId: number, targetColumn: string, instruction: string) => Promise<{ success: boolean; customFill: AiReportCustomFill }>;
    suggestFillOptions: (conversionId: number, targetColumn: string, instruction?: string) => Promise<{ success: boolean; confident: boolean; options?: string[]; reason?: string }>;
    onSave: (columnId: string, rule: { sourceColumn: string | null; customFill: AiReportCustomFill | null }) => void;
    onChooseDataSet: (columnId: string) => void;
}

const ColumnFillWizard: React.FC<ColumnFillWizardProps> = ({
    open, onClose, column, rule, sourceHeaders, conversionId,
    resolveFillInstruction, suggestFillOptions, onSave, onChooseDataSet,
}) => {
    const [mode, setMode] = useState<Mode | null>(null);
    const [sourceDraft, setSourceDraft] = useState('');
    const [constantDraft, setConstantDraft] = useState('');
    const [aiInstruction, setAiInstruction] = useState('');
    const [aiResolving, setAiResolving] = useState(false);
    const [aiResolved, setAiResolved] = useState<AiReportCustomFill | null>(null);
    const [dropdownInstruction, setDropdownInstruction] = useState('');
    const [dropdownOptionsDraft, setDropdownOptionsDraft] = useState('');
    const [dropdownValueDraft, setDropdownValueDraft] = useState('');
    const [dropdownSuggesting, setDropdownSuggesting] = useState(false);
    const [checkboxDraft, setCheckboxDraft] = useState(true);

    useEffect(() => {
        if (!open) return;
        setMode(null);
        setSourceDraft(rule?.sourceColumn || '');
        setConstantDraft('');
        setAiInstruction('');
        setAiResolved(null);
        setAiResolving(false);
        setDropdownInstruction('');
        setDropdownOptionsDraft('');
        setDropdownValueDraft('');
        setDropdownSuggesting(false);
        setCheckboxDraft(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, column?.id]);

    if (!column) return null;
    const label = column.displayName || column.originalName;
    const dropdownOptionsList = dropdownOptionsDraft.split(',').map(o => o.trim()).filter(Boolean);

    const handleChooseMethod = (m: Mode) => {
        if (m === 'dataset') {
            onChooseDataSet(column.id);
            onClose();
            return;
        }
        setMode(m);
    };

    const handleResolveAi = async () => {
        if (!conversionId || !aiInstruction.trim()) return;
        setAiResolving(true);
        try {
            const res = await resolveFillInstruction(conversionId, column.id, aiInstruction.trim());
            if (res.customFill.mode === 'unsupported') {
                showErrorToast(res.customFill.reason || "Couldn't work out how to fill that column from your instruction");
                return;
            }
            setAiResolved(res.customFill);
        } catch (err: any) {
            showErrorToast(err?.response?.data?.message || 'Failed to resolve that instruction');
        } finally {
            setAiResolving(false);
        }
    };

    const handleSuggestOptions = async () => {
        if (!conversionId) return;
        setDropdownSuggesting(true);
        try {
            const res = await suggestFillOptions(conversionId, column.id, dropdownInstruction.trim() || undefined);
            if (!res.confident) {
                showErrorToast(res.reason || 'Not confident enough to suggest options — please enter them manually');
                return;
            }
            setDropdownOptionsDraft((res.options || []).join(', '));
        } catch (err: any) {
            showErrorToast(err?.response?.data?.message || 'Failed to suggest options');
        } finally {
            setDropdownSuggesting(false);
        }
    };

    const canSave = mode === 'source' ? !!sourceDraft
        : mode === 'constant' ? !!constantDraft.trim()
            : mode === 'dropdown' ? dropdownOptionsList.length > 0 && !!dropdownValueDraft
                : mode === 'checkbox' ? true
                    : false;

    const handleSave = () => {
        if (!mode) return;
        if (mode === 'source') {
            onSave(column.id, { sourceColumn: sourceDraft, customFill: null });
        } else if (mode === 'constant') {
            onSave(column.id, { sourceColumn: null, customFill: { mode: 'constant', value: constantDraft.trim() } });
        } else if (mode === 'dropdown') {
            onSave(column.id, { sourceColumn: null, customFill: { mode: 'dropdown', options: dropdownOptionsList, value: dropdownValueDraft } });
        } else if (mode === 'checkbox') {
            onSave(column.id, { sourceColumn: null, customFill: { mode: 'checkbox', value: checkboxDraft ? 'Yes' : 'No' } });
        }
        onClose();
    };

    const handleSaveAi = () => {
        if (!aiResolved) return;
        onSave(column.id, { sourceColumn: null, customFill: aiResolved });
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} size="lg">
            <DialogTitle>How do you want to fill "{label}"?</DialogTitle>
            <DialogBody>
                {mode === null && (
                    <div className="space-y-2">
                        {METHODS.map(m => (
                            <button
                                key={m.mode}
                                type="button"
                                onClick={() => handleChooseMethod(m.mode)}
                                className="flex w-full items-start gap-3 rounded-lg border border-zinc-950/10 p-3 text-left transition hover:border-violet-400 hover:bg-violet-50/50 dark:border-white/10 dark:hover:bg-violet-500/10"
                            >
                                <span className="mt-0.5 shrink-0 text-violet-600">{m.icon}</span>
                                <span className="min-w-0">
                                    <span className="block text-sm font-medium text-zinc-950 dark:text-white">{m.title}</span>
                                    <span className="block text-xs text-zinc-500">{m.description}</span>
                                </span>
                            </button>
                        ))}
                    </div>
                )}

                {mode === 'source' && (
                    <div className="space-y-3">
                        <Text className="text-sm font-medium">Which column in your source file has this value?</Text>
                        <Select value={sourceDraft} onChange={(e) => setSourceDraft(e.target.value)}>
                            <option value="">Select a column&hellip;</option>
                            {sourceHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                        </Select>
                    </div>
                )}

                {mode === 'constant' && (
                    <div className="space-y-3">
                        <Text className="text-sm font-medium">What value should every row get?</Text>
                        <Input
                            autoFocus
                            placeholder="e.g. Sarawak"
                            value={constantDraft}
                            onChange={(e) => setConstantDraft(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && canSave && handleSave()}
                        />
                    </div>
                )}

                {mode === 'ai' && (
                    <div className="space-y-3">
                        <Text className="text-sm font-medium">Describe what should go in this column</Text>
                        <Input
                            autoFocus
                            placeholder='e.g. "always Sarawak"'
                            value={aiInstruction}
                            onChange={(e) => { setAiInstruction(e.target.value); setAiResolved(null); }}
                            onKeyDown={(e) => e.key === 'Enter' && handleResolveAi()}
                        />
                        {!aiResolved && (
                            <Button outline onClick={handleResolveAi} disabled={aiResolving || !aiInstruction.trim()}>
                                {aiResolving ? 'Resolving…' : 'Resolve'}
                            </Button>
                        )}
                        {aiResolved && (
                            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-500/30 dark:bg-amber-500/10">
                                {describeFill({ customFill: aiResolved })}
                            </div>
                        )}
                    </div>
                )}

                {mode === 'dropdown' && (
                    <div className="space-y-3">
                        <Text className="text-sm font-medium">What are the fixed options?</Text>
                        <div className="flex items-center gap-2">
                            <Input
                                placeholder='Optional: tell AI what this is, e.g. "Malaysia states"'
                                value={dropdownInstruction}
                                onChange={(e) => setDropdownInstruction(e.target.value)}
                            />
                            <Button outline onClick={handleSuggestOptions} disabled={dropdownSuggesting}>
                                {dropdownSuggesting ? '…' : 'Suggest with AI'}
                            </Button>
                        </div>
                        <Input
                            placeholder="Options, comma-separated"
                            value={dropdownOptionsDraft}
                            onChange={(e) => setDropdownOptionsDraft(e.target.value)}
                        />
                        <Select
                            value={dropdownValueDraft}
                            onChange={(e) => setDropdownValueDraft(e.target.value)}
                            disabled={dropdownOptionsList.length === 0}
                        >
                            <option value="">Pick a value&hellip;</option>
                            {dropdownOptionsList.map(o => <option key={o} value={o}>{o}</option>)}
                        </Select>
                    </div>
                )}

                {mode === 'checkbox' && (
                    <div className="space-y-3">
                        <Text className="text-sm font-medium">What should every row get?</Text>
                        <label className="flex items-center gap-2">
                            <Checkbox checked={checkboxDraft} onChange={(checked) => setCheckboxDraft(!!checked)} />
                            <Text>{checkboxDraft ? 'Yes' : 'No'}</Text>
                        </label>
                    </div>
                )}
            </DialogBody>
            <DialogActions>
                {mode === null ? (
                    <Button plain onClick={onClose}>Cancel</Button>
                ) : (
                    <>
                        <Button plain onClick={() => setMode(null)}>
                            <ArrowLeftIcon />
                            Back
                        </Button>
                        {mode === 'ai' ? (
                            aiResolved && <Button onClick={handleSaveAi}>Use this</Button>
                        ) : (
                            <Button onClick={handleSave} disabled={!canSave}>Save</Button>
                        )}
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ColumnFillWizard;
